import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  LANGUAGE_NAMES,
  SUPPORTED_LANGUAGES,
} from '../../services/translations/languages';
import { RootState } from '../../store/store';
import { updateUserLanguage as updateUserLanguageAction } from '../../store/users/actions';
import '../../style/layout/LanguageFlags.css';

// Small flags drawn in the proportions 3:2, so no image files are needed
const FLAGS: { [language: string]: JSX.Element } = {
  en: (
    <svg viewBox="0 0 60 40" aria-hidden="true">
      <rect width="60" height="40" fill="#012169" />
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#fff" strokeWidth="8" />
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#C8102E" strokeWidth="3" />
      <path d="M30,0 V40 M0,20 H60" stroke="#fff" strokeWidth="12" />
      <path d="M30,0 V40 M0,20 H60" stroke="#C8102E" strokeWidth="7" />
    </svg>
  ),
  fi: (
    <svg viewBox="0 0 18 12" aria-hidden="true">
      <rect width="18" height="12" fill="#fff" />
      <rect x="5" width="3" height="12" fill="#002F6C" />
      <rect y="4.5" width="18" height="3" fill="#002F6C" />
    </svg>
  ),
  sv: (
    <svg viewBox="0 0 16 10.67" aria-hidden="true">
      <rect width="16" height="10.67" fill="#006AA7" />
      <rect x="5" width="2" height="10.67" fill="#FECC00" />
      <rect y="4.33" width="16" height="2" fill="#FECC00" />
    </svg>
  ),
};

/**
 * Language of the user interface as small flags below the user name, visible in every
 * view. The choice is saved for the user.
 */
const LanguageFlags = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const user = useSelector((state: RootState) => state.users.current);

  if (user === null) {
    return null;
  }

  return (
    <div
      className="language-flags"
      role="group"
      aria-label="Language / Kieli / Språk"
    >
      {SUPPORTED_LANGUAGES.map((language) => (
        <button
          aria-label={LANGUAGE_NAMES[language]}
          aria-pressed={i18n.language === language}
          className={`language-flag${
            i18n.language === language ? ' active' : ''
          }`}
          key={language}
          lang={language}
          onClick={() => dispatch(updateUserLanguageAction(language))}
          title={LANGUAGE_NAMES[language]}
          type="button"
        >
          {FLAGS[language]}
        </button>
      ))}
    </div>
  );
};

export default LanguageFlags;
